import React, { useState, useEffect } from "react"
import { navigate } from "vike/client/router"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChartBarIcon,
  PauseIcon,
  PlayIcon,
} from "@heroicons/react/24/outline"
import { GetPacks, DeletePack, UpdatePack } from "./api"
import AddPackDialog from "./AddPackDialog"
import PackViewer from "./PackViewer"
import { twMerge } from "tailwind-merge"

export default function LicensePacks() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [selectedPackId, setSelectedPackId] = useState(null)
  const [allPacks, setAllPacks] = useState([])
  const [addPackDialog, setAddPackDialog] = useState({
    isOpen: false,
    pack: null,
  })

  // Check URL for pack ID and monitor URL changes
  useEffect(() => {
    const checkURL = () => {
      if (typeof window !== "undefined") {
        const match = window.location.pathname.match(/\/packs\/([^\/]+)/)
        if (match) {
          setSelectedPackId(match[1])
        } else {
          setSelectedPackId(null)
        }
      }
    }

    // Check on mount
    checkURL()

    // Listen for browser navigation (back/forward)
    const handlePopState = () => checkURL()
    window.addEventListener('popstate', handlePopState)

    // Also check URL periodically to catch programmatic navigation
    const checkInterval = setInterval(checkURL, 100)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      clearInterval(checkInterval)
    }
  }, [])

  // Handle pack selection
  const handleViewPack = (packId) => {
    // Just navigate - the URL change will trigger the state update
    navigate(`/admin/product-licensing/packs/${packId}`)
  }

  // Handle closing pack viewer
  const handleClosePack = () => {
    setSelectedPackId(null)
    navigate("/admin/product-licensing/packs")
  }

  // Handle select all
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle edit pack
  const handleEditPack = (pack) => {
    setAddPackDialog({
      isOpen: true,
      pack: pack,
    })
  }

  // Handle toggle pack state
  const handleTogglePackState = async (pack) => {
    const newState = pack.state === "active" ? "paused" : "active"
    const action = newState === "active" ? "activate" : "pause"

    showPromptDialog({
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      message: `Are you sure you want to ${action} "${pack.name}"?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      confirmVariant: newState === "active" ? "primary" : "warning",
      onConfirm: async () => {
        try {
          const response = await UpdatePack({
            packId: pack._id,
            state: newState,
          })

          if (response.result) {
            showNotification({
              message: `Pack ${action}d successfully`,
              className: "bg-admin_primary text-white",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.message || response.error || `Failed to ${action} pack`,
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = `An error occurred while ${action}ing pack`
          
          // Check if it's an axios error with response data
          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }
          
          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle delete pack
  const handleDeletePack = (pack) => {
    // Check if pack is primary
    if (pack.primary) {
      showNotification({
        message: `The pack "${pack.name}" is primary. Remove its primary status before deletion.`,
        className: "bg-admin_warning text-white",
      })
      return
    }

    showPromptDialog({
      title: "Delete Pack",
      message: `Are you sure you want to delete "${pack.name}"? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          const response = await DeletePack(pack._id)

          if (response.result) {
            showNotification({
              message: "Pack deleted successfully",
              className: "bg-admin_primary text-white",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.message || response.error || "Failed to delete pack",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = "An error occurred while deleting pack"
          
          // Check if it's an axios error with response data
          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }
          
          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle bulk delete selected packs
  const handleDeleteSelected = (allItems) => {
    if (selectedItems.length === 0) return

    // Check for primary packs in selection
    const selectedPacks = allItems.filter(item => selectedItems.includes(item._id))
    const primaryPacks = selectedPacks.filter(pack => pack.primary)

    if (primaryPacks.length > 0) {
      const primaryNames = primaryPacks.map(pack => pack.name).join(", ")
      showNotification({
        message: `The pack${primaryPacks.length > 1 ? "s" : ""} "${primaryNames}" ${primaryPacks.length > 1 ? "are" : "is"} primary. Remove their primary status before deletion.`,
        className: "bg-admin_warning text-white",
      })
      return
    }

    showPromptDialog({
      title: "Delete Packs",
      message: `Are you sure you want to delete ${
        selectedItems.length
      } pack${
        selectedItems.length === 1 ? "" : "s"
      }? This action cannot be undone and will affect all licenses in these packs.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          let deletedCount = 0
          let failedCount = 0

          // Delete packs one by one since there's no bulk delete API
          for (const packId of selectedItems) {
            try {
              const response = await DeletePack(packId)
              if (response.result) {
                deletedCount++
              } else {
                failedCount++
                // Show specific error for failed deletions - prioritize message field
                const errorMessage = response.message || response.error || "Failed to delete pack"
                showNotification({
                  message: errorMessage,
                  className: "bg-admin_error text-white",
                })
              }
            } catch (error) {
              failedCount++
              // Extract error message from different possible sources
              let errorMessage = "An error occurred while deleting pack"
              
              // Check if it's an axios error with response data
              if (error.response && error.response.data) {
                const responseData = error.response.data
                errorMessage = responseData.message || responseData.error || error.message
              } else if (error.message) {
                errorMessage = error.message
              }
              
              showNotification({
                message: errorMessage,
                className: "bg-admin_error text-white",
              })
            }
          }

          if (deletedCount > 0) {
            showNotification({
              message: `${deletedCount} pack${
                deletedCount === 1 ? "" : "s"
              } deleted successfully${failedCount > 0 ? `, ${failedCount} failed` : ""}`,
              className: "bg-admin_primary text-white",
            })
          }
          
          // Always clear selection and refresh if any operation was attempted
          setSelectedItems([])
          setRefreshKey((prev) => prev + 1)
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = "An error occurred while deleting packs"
          
          // Check if it's an axios error with response data
          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }
          
          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Style overrides for DataTable
  const styleOverrides = {
    table: "w-full table-fixed",
    header: "w-full h-4 flex justify-between items-end gap-2 mt-6 mb-4",
    searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    getRowStyle: (_, item) => {
      if (item && selectedItems.includes(item._id)) {
        return "bg-admin_canvas hover:bg-admin_canvas/70"
      }
    },
  }

  // Column definitions
  const columns = [
    {
      id: "name",
      title: "Pack Name",
      headerClassName: "w-1/3 xl:w-1/4",
      render: (_, item) => (
        <div onClick={() => handleViewPack(item._id)}>
          <div className="flex items-center space-x-2">
            <Typography
              variant="admin_label"
              className="cursor-pointer hover:text-admin_primary my-0"
            >
              {item.name}
            </Typography>

            {/* Show external badge if external, otherwise show primary badge if primary */}
            {item.external ? (
              <Typography 
                variant="admin_txt2"
                className="text-white font-medium bg-violet-600 rounded-full px-3 py-1"
              >
                external
              </Typography>
            ) : (
              item.primary && (
                <Typography 
                  variant="admin_txt2"
                  className="text-admin_text_inv font-medium bg-admin_progress rounded-full px-3 py-1"
                >
                  primary
                </Typography>
              )
            )}
          </div>

          {item.description && (
            <Typography
              variant="admin_txt2_dim"
              className="mt-2"
            >
              {item.description}
            </Typography>
          )}
        </div>
      ),
    },
    {
      id: "licenses",
      title: "Licenses",
      headerClassName: "w-[100px] text-center",
      render: (_, item) => {
        const totalLicenses = item.statistics?.licenses || 0
        const activeLicenses = item.statistics?.active || 0
        const usagePercent = totalLicenses > 0 ? Math.round((activeLicenses / totalLicenses) * 100) : 0
        
        return (
          <div className="flex flex-col items-center">
            <Typography 
              variant="admin_label" 
              className="my-0"
            >
              {totalLicenses}
            </Typography>

            <Typography
              variant="admin_txt2"
              className={`font-semibold ${
                totalLicenses === 0
                  ? "text-admin_error"
                  : usagePercent > 80
                  ? "text-admin_warning"
                  : "text-admin_success"
              }`}
            >
              {totalLicenses === 0 ? "Empty" : `${usagePercent}% used`}
            </Typography>
          </div>
        )
      },
    },
    {
      id: "seats",
      title: "Seats",
      headerClassName: "w-[120px] text-center",
      render: (_, item) => (
        <div className="flex flex-col items-center mt-2">
          <Typography 
            variant="admin_label" 
            className="text-center my-0"
          >
            {item.statistics?.seats || 0}
          </Typography>

          {/* Only show progress bar and usage for non-external packs */}
          {!item.external && (
            <>
              <div className="w-full h-2 bg-admin_neutral_1 rounded-full mt-1">
                <div
                  className="bg-admin_primary h-2 rounded-full transition-all"
                  style={{ width: `${item.statistics?.seatUsage || 0}%` }}
                />
              </div>

              <Typography
                variant="admin_txt2_dim"
                className="text-center mt-1"
              >
                {item.statistics?.seatUsage || 0}% used
              </Typography>
            </>
          )}

          {/* Show "External" label for external packs */}
          {item.external && (
            <Typography
              variant="admin_txt2_dim"
              className="text-center mt-1"
            >
              Managed externally
            </Typography>
          )}
        </div>
      ),
    },
    {
      id: "state",
      title: "State",
      headerClassName: "w-[100px] text-center",
      render: (_, item) => (
        <Typography
          variant="admin_txt2"
          className={twMerge(
            "block w-fit text-center font-medium rounded-full px-3 py-1 mx-auto",
            item.state === "active"
              ? "bg-admin_success text-white"
              : "bg-admin_warning text-white"
          )}
        >
          {item.state}
        </Typography>
      ),
    },
    {
      id: "created",
      title: "Created",
      headerClassName: "w-[130px] text-center",
      render: (_, item) => (
        <Typography 
          variant="admin_txt1_dim" 
          className="block text-center"
        >
          {new Date(item.created).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[190px] text-center",
      render: (_, item) => (
        <div className="flex justify-center space-x-1">
          <Button
            variant="admin_icon"
            className="px-2"
            onClick={() => handleViewPack(item._id)}
            title="View pack details"
          >
            <EyeIcon className="w-5 h-5" />
          </Button>

          <Button
            variant="admin_icon"
            className="px-2"
            onClick={() => handleEditPack(item)}
            title="Edit pack"
          >
            <PencilIcon className="w-5 h-5" />
          </Button>

          <Button
            variant="admin_icon"
            className={`px-2 transition-colors ${
              item.state === "active"
                ? "text-admin_warning"
                : "text-admin_success"
            }`}
            onClick={() => handleTogglePackState(item)}
            title={item.state === "active" ? "Pause pack" : "Activate pack"}
          >
            {item.state === "active" ? (
              <PauseIcon className="w-5 h-5" />
            ) : (
              <PlayIcon className="w-5 h-5" />
            )}
          </Button>

          <Button
            variant="admin_icon"
            className="text-admin_error hover:text-admin_error/80 transition-colors px-2"
            onClick={() => handleDeletePack(item)}
            title="Delete pack"
          >
            <TrashIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ]

  // Show pack viewer if selected
  if (selectedPackId) {
    return <PackViewer packId={selectedPackId} onClose={handleClosePack} />
  }

  return (
    <div className="p-6">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          License Packs
        </Typography>

        <Button
          variant="admin_primary"
          className="whitespace-nowrap flex items-center"
          onClick={() => setAddPackDialog({ isOpen: true, pack: null })}
        >
          <PlusCircleIcon className="w-5 h-5 mr-1" />
          Add
        </Button>
      </div>

      <DataTable
        variant="admin_browser"
        apiMethod={GetPacks}
        enableSelection={true}
        selectedItems={selectedItems}
        onSelectionChange={(ids) => setSelectedItems(ids)}
        bulkActions={[
          {
            label: "Delete",
            variant: "admin_error",
            icon: <TrashIcon className="w-5 h-5" />,
            onClick: () => handleDeleteSelected(allPacks),
          }
        ]}
        itemIdField="_id"
        searchField="name description"
        sort={{ created: -1 }}
        localUserPreferencesKey="admin.licensing.packs"
        renders={columns}
        refreshKey={refreshKey}
        preferredHeight="60vh"
        itemHeight="h-20"
        onDataUpdate={(data) => setAllPacks(data.items || [])}
        noData={
          <div className="text-center py-8">
            <ChartBarIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />
            <Typography
              variant="admin_body_dim"
            >
              No license packs found
            </Typography>

            <Button
              variant="admin_primary"
              className="mt-4"
              onClick={() => setAddPackDialog({ isOpen: true, pack: null })}
            >
              Create First Pack
            </Button>
          </div>
        }
        tableClassName="table-fixed"
        styleOverrides={styleOverrides}
      />

      {/* Add/Edit Pack Dialog */}
      <AddPackDialog
        isOpen={addPackDialog.isOpen}
        pack={addPackDialog.pack}
        onClose={() => setAddPackDialog({ isOpen: false, pack: null })}
        onPackSaved={() => {
          setRefreshKey((prev) => prev + 1)
          setAddPackDialog({ isOpen: false, pack: null })
        }}
      />
    </div>
  )
}
